/**
 * Service API centralisé
 * Gère tous les appels vers le backend Express
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

/**
 * Types pour l'API V1 Substance
 */
export interface SubstanceV1Response {
  success: boolean
  fiche?: SubstanceFiche
  error?: string
}

export interface SubstanceFiche {
  identite: {
    cas_number: string
    nom_fr: string | null
    nom_en: string | null
    synonymes: string[]
    formule_moleculaire: string | null
    masse_moleculaire: string | null
  }
  dangers: {
    codes_h: string[]
    pictogrammes: GHSPictogram[]
    cmr: CMRStatus | null
  }
  sources: {
    inrs: SourceINRS
    anses: SourceANSES
    echa: SourceECHA
    agritox: SourceAGRITOX
  }
  epi: EPIRecommendations
  meta: {
    sources_trouvees: string[]
    nombre_sources: number
  }
}

export interface GHSPictogram {
  code: string
  name: string
  description: string
}

export interface CMRStatus {
  est_cmr: boolean
  categorie: string | null
  detail: string | null
}

export interface SourceINRS {
  disponible: boolean
  date_donnees: string | null
  url: string
  vlep_8h: { valeur: number; unite: string } | null
  vlep_court_terme: { valeur: number; unite: string } | null
  mentions: string | null
  message?: string
}

export interface SourceANSES {
  disponible: boolean
  date_donnees: string | null
  url: string
  vtr_orale: { valeur: number; unite: string } | null
  vtr_inhalation: { valeur: number; unite: string } | null
  effet_critique: string | null
  message?: string
}

export interface SourceECHA {
  disponible: boolean
  date_donnees: string | null
  url: string
  svhc: boolean
  raison_inclusion: string | null
  message?: string
}

export interface SourceAGRITOX {
  disponible: boolean
  date_donnees: string | null
  url: string
  dja: { valeur: number; unite: string } | null
  arfd: { valeur: number; unite: string } | null
  aoel: { valeur: number; unite: string } | null
  message?: string
}

export interface EPIRecommendations {
  protection_respiratoire: { type: string; norm: string }
  protection_mains: { type: string; norm: string }
  protection_yeux: { type: string; norm: string }
  protection_corps: { type: string; norm: string }
  notes: string[]
  recommandations_documentees: boolean
}

// Callback pour mettre à jour le quota depuis les headers
let quotaUpdateCallback: ((remaining: number) => void) | null = null

export function setQuotaUpdateCallback(callback: (remaining: number) => void) {
  quotaUpdateCallback = callback
}

/**
 * Fetch wrapper avec gestion d'erreurs et extraction quota
 */
async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${endpoint}`

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include', // Toujours inclure les cookies
    ...options,
  })

  // Extraire le quota des headers si présent
  const quotaRemaining = response.headers.get('X-Quota-Remaining')
  if (quotaRemaining && quotaUpdateCallback) {
    const remaining = parseInt(quotaRemaining, 10)
    if (!isNaN(remaining)) {
      quotaUpdateCallback(remaining)
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))

    // Gérer spécifiquement l'erreur de quota (402 PAYWALL)
    if (response.status === 402 || errorData.error_code === 'PAYWALL') {
      throw new Error(`PAYWALL: ${errorData.message || 'Quota de recherches atteint'}`)
    }

    throw new Error(errorData.error || `Erreur HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * API V1 - Fiche Substance
 */
export const substanceAPI = {
  /**
   * Récupérer une fiche substance par numéro CAS
   */
  getByCAS: async (cas: string): Promise<SubstanceFiche> => {
    const response = await fetchAPI<SubstanceV1Response>(`/api/v1/substance/${cas}`)

    if (!response.success || !response.fiche) {
      throw new Error(response.error || 'Substance non trouvée')
    }

    return response.fiche
  },
}

/**
 * API V2 - Recherche
 */
export const searchAPI = {
  /**
   * Recherche multi-sources
   */
  search: async (query: string, sources?: string[]): Promise<unknown> => {
    const params = new URLSearchParams({ q: query })
    if (sources?.length) {
      params.set('sources', sources.join(','))
    }

    return fetchAPI(`/api/v2/search?${params}`)
  },
}

/**
 * API Status
 */
export const statusAPI = {
  getStatus: () => fetchAPI('/api/v2/status'),
  getHealth: () => fetchAPI('/health'),
}

/**
 * Types Auth
 */
export interface AuthUser {
  id: number
  email: string
  emailVerified: boolean
  quotaRemaining: number
  quotaResetAt?: string
  subscriptionStatus: 'free' | 'trial' | 'active' | 'cancelled'
  subscriptionExpiresAt?: string
  isSubscribed: boolean
}

interface AuthStartResponse {
  success: boolean
  message?: string
  expiresIn?: number
  error?: string
  error_code?: string
}

interface AuthVerifyResponse {
  success: boolean
  user?: AuthUser
  expiresAt?: string
  error?: string
  error_code?: string
}

interface AuthMeResponse {
  success: boolean
  authenticated: boolean
  user: AuthUser | null
}

/**
 * API Auth - Authentification par email OTP
 */
export const authAPI = {
  /**
   * Démarre l'authentification - envoie un code OTP par email
   */
  start: async (email: string): Promise<AuthStartResponse> => {
    return fetchAPI('/api/auth/start', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  /**
   * Vérifie le code OTP et crée une session
   */
  verify: async (email: string, code: string): Promise<AuthVerifyResponse> => {
    return fetchAPI('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
      credentials: 'include', // Important pour les cookies
    })
  },

  /**
   * Récupère l'utilisateur courant
   */
  me: async (): Promise<AuthMeResponse> => {
    return fetchAPI('/api/me', {
      credentials: 'include',
    })
  },

  /**
   * Déconnexion
   */
  logout: async (): Promise<{ success: boolean }> => {
    return fetchAPI('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
  },

  /**
   * Récupère le quota restant
   */
  quota: async (): Promise<{ success: boolean; quota: { remaining: number; isSubscribed: boolean; resetAt?: string } }> => {
    return fetchAPI('/api/auth/quota', {
      credentials: 'include',
    })
  },
}
